/**
 * Meta / paid-social intake attribution.
 *
 * Canonical LA multiple-myeloma patient-acquisition landing URL:
 * https://curegate.ai/patient-registration?mode=quick&condition=multiple_myeloma&utm_source=meta&utm_medium=paid_social&utm_campaign=mm_la_2026
 *
 * Extra keys are not sent on public-intake-gateway (deployed contract). After a
 * successful commit, persist_intake_attribution writes UTMs onto intake_cases —
 * the same post-commit pattern as persist_intake_chosen_site. Document-upload
 * (submit_transactional_intake_v4) uses the same persist call.
 */

import { canonicalizeCancerType, cancerTypeLabel } from './cancerTypes';

export const META_MM_LANDING_PATH =
  '/patient-registration?mode=quick&condition=multiple_myeloma&utm_source=meta&utm_medium=paid_social&utm_campaign=mm_la_2026';

export const META_MM_LANDING_URL = `https://curegate.ai${META_MM_LANDING_PATH}`;

const META_SOURCES = new Set(['meta', 'facebook', 'fb', 'instagram']);

function readParam(params, ...keys) {
  for (const key of keys) {
    const value = params.get(key);
    if (value && String(value).trim()) return String(value).trim();
  }
  return null;
}

export function parseIntakeAttribution(search) {
  const params = typeof search === 'string'
    ? new URLSearchParams(search.startsWith('?') ? search : `?${search}`)
    : (search instanceof URLSearchParams ? search : new URLSearchParams());

  const utmSource = readParam(params, 'utm_source');
  const utmMedium = readParam(params, 'utm_medium');
  const utmCampaign = readParam(params, 'utm_campaign');
  const utmContent = readParam(params, 'utm_content');
  const fbclid = readParam(params, 'fbclid');
  const adCampaignId = readParam(params, 'ad_campaign_id', 'utm_campaign');
  const cancerType = canonicalizeCancerType(readParam(params, 'condition', 'cancer_type'));
  const sourceHint = readParam(params, 'acquisition_source', 'utm_source');
  const acquisitionSource = META_SOURCES.has(String(sourceHint || '').toLowerCase())
    ? 'meta'
    : (sourceHint ? String(sourceHint).toLowerCase() : null);

  return {
    cancer_type: cancerType,
    acquisition_source: acquisitionSource,
    utm_source: utmSource,
    utm_medium: utmMedium,
    utm_campaign: utmCampaign,
    utm_content: utmContent,
    fbclid,
    ad_campaign_id: adCampaignId,
  };
}

export function attributionHasPayload(attribution) {
  if (!attribution || typeof attribution !== 'object') return false;
  return Boolean(
    attribution.acquisition_source
    || attribution.utm_source
    || attribution.utm_medium
    || attribution.utm_campaign
    || attribution.utm_content
    || attribution.fbclid
    || attribution.ad_campaign_id
  );
}

export function isMetaAcquisition(attribution) {
  const source = String(attribution?.acquisition_source || '').toLowerCase();
  const utm = String(attribution?.utm_source || '').toLowerCase();
  return source === 'meta' || utm === 'meta' || META_SOURCES.has(utm);
}

export function attributionPayload(attribution) {
  if (!attributionHasPayload(attribution) && !attribution?.cancer_type) return null;
  return {
    acquisition_source: attribution.acquisition_source || null,
    utm_source: attribution.utm_source || null,
    utm_medium: attribution.utm_medium || null,
    utm_campaign: attribution.utm_campaign || null,
    utm_content: attribution.utm_content || null,
    fbclid: attribution.fbclid || null,
    ad_campaign_id: attribution.ad_campaign_id || null,
    cancer_type: attribution.cancer_type || null,
  };
}

export function metaIntakeAlertSubject(attribution) {
  const label = cancerTypeLabel(attribution?.cancer_type) || 'Oncology';
  return `New Meta ad patient — ${label}`;
}
